import { JSX } from "preact";
import classNames from "classnames";

/**
 * FeatureCard - A unified card component system for displaying features with icons
 *
 * This component provides a flexible layout for showcasing features, partners, or benefits
 * with icons that can be positioned either at the top or on the left side.
 *
 * Example usage (top icon):
 * ```mdx
 * <FeatureCard position="top">
 *   <FeatureCardIcon>
 *     <img src="/img/section-images/FaceIcon.svg" alt="Productivity" />
 *   </FeatureCardIcon>
 *   <FeatureCardContent>
 *     <h3>Boost productivity</h3>
 *     <p>Bring your team together with tools that make collaboration effortless.</p>
 *   </FeatureCardContent>
 * </FeatureCard>
 * ```
 *
 * Example usage (left icon):
 * ```mdx
 * <FeatureCard position="left" accent transparent>
 *   <FeatureCardIcon>
 *     <img src="/img/section-images/FaceIcon.svg" alt="Cloud UCaaS" />
 *   </FeatureCardIcon>
 *   <FeatureCardContent>
 *     <h3>Cloud-based UCaaS</h3>
 *     <p>Scalable, feature-rich platforms like Microsoft Teams, Zoom, and RingCentral.</p>
 *   </FeatureCardContent>
 * </FeatureCard>
 * ```
 */

export function FeatureCard(props: {
  children: JSX.Element;
  position?: "top" | "left";
  accent?: boolean;
  transparent?: boolean
}) {
  return (
    <div class={classNames("feature-card", {
      "accent": props.accent,
      "transparent": props.transparent,
      "layout-left": props.position === "left"
    })}>
      {props.children}
    </div>
  );
}

export function FeatureCardIcon(props: { children: JSX.Element }) {
  return (
    <div class="feature-card-icon">
      {props.children}
    </div>
  );
}

export function FeatureCardContent(props: { children: JSX.Element }) {
  return (
    <div class="feature-card-content">
      {props.children}
    </div>
  );
}